/* ============================================================================
 * useHelperFunctions.ts - Helper functions for tree and node operations
 * ============================================================================ */

import { useCallback, useMemo } from 'react';
import type { ProjectData, ProjectNode, ProjectNodeWithComputed } from '@/shared/types/projectData';
import { buildTree, buildNodeMap } from '@/shared/utils/projectDataUtils';

interface UseHelperFunctionsParams {
  projectData: ProjectData | null;
  filteredNodes: ProjectNodeWithComputed[];
  expandedNodeIds: Set<string>;
}

/**
 * Hook for helper functions
 */
export function useHelperFunctions({
  projectData,
  filteredNodes,
  expandedNodeIds
}: UseHelperFunctionsParams) {
  
  // Computed values
  const nodeMap = useMemo(() => projectData ? buildNodeMap(filteredNodes) : new Map(), [projectData, filteredNodes]);
  const computedTreeNodes = useMemo(
    () => projectData ? buildTree({ ...projectData, nodes: filteredNodes }) : [],
    [projectData, filteredNodes]
  );
  
  // Get renderable nodes with visibility state
  const getRenderableNodes = useCallback(() => {
    if (!projectData) return [];
    
    // Simple hierarchical visibility - exactly like the backup implementation
    const renderable: (ProjectNodeWithComputed & { isVisible: boolean })[] = [];
    
    const traverse = (nodes: ProjectNodeWithComputed[], parentIsExpanded: boolean) => {
      for (const node of nodes) {
        // Node is visible if its parent is expanded (hierarchical visibility only)
        const isVisible = parentIsExpanded;
        
        renderable.push({ ...node, isVisible });
        
        if (node.children) {
          // Children are visible if this node is visible AND expanded
          traverse(node.children, isVisible && expandedNodeIds.has(node.id));
        }
      }
    };
    
    traverse(computedTreeNodes, true);
    return renderable;
  }, [computedTreeNodes, expandedNodeIds, projectData]);

  // Get node by ID
  const getNode = useCallback((nodeId: string) => nodeMap.get(nodeId) || null, [nodeMap]);
  
  // Get visible nodes
  const getVisibleNodes = useCallback(() => {
    if (!projectData) return [];
    const visible: ProjectNode[] = [];
    const traverse = (nodes: ProjectNodeWithComputed[]) => {
      for (const node of nodes) {
        visible.push(node);
        if (expandedNodeIds.has(node.id) && node.children) {
          traverse(node.children);
        }
      }
    };
    traverse(computedTreeNodes);
    return visible;
  }, [computedTreeNodes, expandedNodeIds, projectData]);
  
  return {
    nodeMap,
    treeNodes: computedTreeNodes,
    getRenderableNodes,
    getNode,
    getVisibleNodes
  };
}
